import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Typography,
    TextField,
    IconButton,
    Rating,
    FormControlLabel,
    Checkbox,
    useMediaQuery,
    useTheme as useMuiTheme,
    Grid,
    Stack,
    type SxProps,
    type Theme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined';
import EditNoteOutlined from '@mui/icons-material/EditNoteOutlined';
import StarOutlineOutlined from '@mui/icons-material/StarOutlineOutlined';
import dayjs, { type Dayjs } from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import CustomButton from '../Button/CustomButton';
import { useDispatch } from 'react-redux';
import { addCompanyReview } from '../../redux/company/actions';
import { ICompanyReview } from '../../redux/company/types';

interface ReviewFormData {
    review_subject: string;
    pros: string;
    cons: string;
    role: string;
    time: string;
    startDate: Date;
    endDate: Date;
    isCurrent: boolean;
    rating: number;
    professionalism: number;
    satisfaction: number;
    creative_quality: number;
    culture: number;
    payment_experience: number;
    reliability: number;
    recommend: number;
}

interface ReviewFormModalProps {
    open: boolean;
    onClose: () => void;
    companyId: string;
    companyName: string;
    editingReview?: ICompanyReview | null;
    onSubmit?: (formData: ReviewFormData) => void;
}

/** Modal shell — deep grey (reference: neutral-900 / #121212). */
const PAGE_BG = '#121212';
/** All text fields + date inputs share this radius (px). */
const FIELD_RADIUS_PX = 14;
const FIELD_BG = '#000000';
const PICKER_POPOVER_BG = '#262626';
const HELPER_MUTED = 'rgba(255,255,255,0.5)';
const PLACEHOLDER_MUTED = 'rgba(255,255,255,0.42)';

function buildReviewTextFieldSx(themeStyle: keyof typeof theme): SxProps<Theme> {
    const accent = theme[themeStyle].highlight;
    return {
        '& .MuiOutlinedInput-root': {
            borderRadius: `${FIELD_RADIUS_PX}px`,
            bgcolor: FIELD_BG,
            color: theme[themeStyle].tag_white,
            fontFamily: FONT_ENUM.MAIN_MEDIUM,
            '& fieldset': {
                borderWidth: 0,
            },
            '&:hover fieldset': {
                borderWidth: 0,
            },
            '&.Mui-focused': {
                boxShadow: `0 0 0 1px ${accent}`,
            },
            '&.Mui-focused fieldset': {
                borderWidth: 0,
            },
        },
        '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
        },
        '& .MuiOutlinedInput-input::placeholder': {
            color: PLACEHOLDER_MUTED,
            opacity: 1,
        },
        '& .MuiFormHelperText-root': {
            color: HELPER_MUTED,
            fontFamily: FONT_ENUM.MAIN_MEDIUM,
            fontSize: '0.75rem',
            mt: 0.75,
            mx: 0,
        },
    };
}

function fieldLabelSx(): SxProps<Theme> {
    return {
        color: HELPER_MUTED,
        fontFamily: FONT_ENUM.MAIN_BOLD,
        fontSize: '0.65rem',
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        mb: 0.75,
        display: 'block',
    };
}

function sectionTitleRowSx(themeStyle: keyof typeof theme): SxProps<Theme> {
    return {
        color: theme[themeStyle].tag_white,
        fontFamily: FONT_ENUM.MAIN_BOLD,
        fontSize: '1rem',
        fontWeight: 700,
    };
}

function employmentDateLabelSx(): SxProps<Theme> {
    return {
        fontSize: '0.65rem',
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
        color: HELPER_MUTED,
        fontFamily: FONT_ENUM.MAIN_BOLD,
        fontWeight: 700,
        mb: 0.75,
        display: 'block',
    };
}

const ReviewFormModal: React.FC<ReviewFormModalProps> = ({
    open,
    onClose,
    companyId,
    companyName,
    editingReview,
    onSubmit,
}) => {
    const { themeStyle } = useThemeContext();
    const muiTheme = useMuiTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
    const dispatch = useDispatch();

    const [formData, setFormData] = useState<ReviewFormData>({
        review_subject: '',
        pros: '',
        cons: '',
        role: '',
        time: '',
        startDate: new Date(),
        endDate: new Date(),
        isCurrent: false,
        rating: 3,
        professionalism: 3,
        satisfaction: 3,
        creative_quality: 3,
        culture: 3,
        payment_experience: 3,
        reliability: 3,
        recommend: 3,
    });

    // Pre-populate form when editing
    useEffect(() => {
        if (editingReview && open) {
            setFormData({
                review_subject: editingReview.review_subject || '',
                pros: editingReview.pros || '',
                cons: editingReview.cons || '',
                role: editingReview.role || '',
                time: editingReview.time || '',
                startDate: new Date(), // Default to current date for editing
                endDate: new Date(), // Default to current date for editing
                isCurrent: editingReview.time === 'Current Employee',
                rating: editingReview.rating || 3,
                professionalism: 3, // Default values since these fields are not in ICompanyReview
                satisfaction: 3,
                creative_quality: 3,
                culture: 3,
                payment_experience: 3,
                reliability: 3,
                recommend: 3,
            });
        } else if (!editingReview && open) {
            // Reset form for new review
            setFormData({
                review_subject: '',
                pros: '',
                cons: '',
                role: '',
                time: '',
                startDate: new Date(),
                endDate: new Date(),
                isCurrent: false,
                rating: 3,
                professionalism: 3,
                satisfaction: 3,
                creative_quality: 3,
                culture: 3,
                payment_experience: 3,
                reliability: 3,
                recommend: 3,
            });
        }
    }, [editingReview, open]);

    const handleInputChange = (field: keyof ReviewFormData, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleStartDateChange = (date: Date) => {
        setFormData(prev => ({
            ...prev,
            startDate: date,
        }));
    };

    const handleEndDateChange = (date: Date) => {
        setFormData(prev => ({
            ...prev,
            endDate: date,
        }));
    };

    const handleCurrentEmployeeChange = (isCurrent: boolean) => {
        setFormData(prev => ({
            ...prev,
            isCurrent,
            // If current employee, set endDate to 0 (epoch time)
            endDate: isCurrent ? new Date(0) : new Date(),
        }));
    };

    const handleSubmit = () => {
        // Calculate duration if not current
        let timeString = '';
        if (!formData.isCurrent) {
            const start = new Date(formData.startDate);
            const end = new Date(formData.endDate);
            const diffTime = Math.abs(end.getTime() - start.getTime());
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            const months = Math.floor(diffDays / 30);
            const years = Math.floor(months / 12);

            if (years > 0) {
                timeString = `${years} year${years > 1 ? 's' : ''}`;
            } else if (months > 0) {
                timeString = `${months} month${months > 1 ? 's' : ''}`;
            } else {
                timeString = `${diffDays} day${diffDays > 1 ? 's' : ''}`;
            }
        } else {
            timeString = 'Current Employee';
        }

        if (editingReview && onSubmit) {
            // Call the parent's onSubmit for editing
            onSubmit(formData);
        } else {
            // Handle new review creation
            const submitData: any = {
                ...formData,
                review_text: formData.pros, // Save pros as review_text as well
                time: timeString,
                company: companyId,
                // If current employee, ensure endDate is 0
                endDate: formData.isCurrent ? new Date(0) : formData.endDate,
            };

            dispatch(addCompanyReview(submitData));
            onClose();
        }
    };

    const renderStarRating = (
        label: string,
        value: number,
        field: keyof ReviewFormData,
        onChange: (value: number) => void,
    ) => (
        <Box>
            <Typography
                sx={{
                    color: theme[themeStyle].tag_white,
                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    fontSize: '0.875rem',
                    mb: 1,
                }}>
                {label}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Rating
                    value={value}
                    onChange={(_, newValue) => onChange(newValue || 3)}
                    precision={0.1}
                    max={5}
                    sx={{
                        '& .MuiRating-iconFilled': {
                            color: theme[themeStyle].highlight,
                        },
                        '& .MuiRating-iconHover': {
                            color: theme[themeStyle].highlight,
                        },
                        '& .MuiRating-iconEmpty': {
                            color: theme[themeStyle].tag_white,
                        },
                    }}
                />
                <Typography
                    sx={{
                        color: theme[themeStyle].highlight,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        minWidth: '40px',
                    }}>
                    {value.toFixed(1)}
                </Typography>
            </Box>
        </Box>
    );

    const reviewTextFieldSx = buildReviewTextFieldSx(themeStyle);

    const reviewDatePickerSlotProps = {
        textField: {
            fullWidth: true,
            placeholder: 'mm/dd/yyyy',
            sx: {
                '& .MuiOutlinedInput-root': {
                    borderRadius: `${FIELD_RADIUS_PX}px`,
                    bgcolor: FIELD_BG,
                    color: theme[themeStyle].tag_white,
                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    '& fieldset': {
                        borderWidth: 0,
                    },
                    '&:hover fieldset': {
                        borderWidth: 0,
                    },
                    '&.Mui-focused': {
                        boxShadow: `0 0 0 1px ${theme[themeStyle].highlight}`,
                    },
                    '&.Mui-focused fieldset': {
                        borderWidth: 0,
                    },
                },
                '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                },
                '& .MuiOutlinedInput-input::placeholder': {
                    color: PLACEHOLDER_MUTED,
                    opacity: 1,
                },
                '& .MuiInputAdornment-root .MuiIconButton-root': {
                    color: 'rgba(255,255,255,0.88)',
                },
            },
        },
        popper: {
            sx: {
                '& .MuiPaper-root': {
                    bgcolor: PICKER_POPOVER_BG,
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: 2,
                    backgroundImage: 'none',
                },
                '& .MuiPickersCalendarHeader-label': { color: HELPER_MUTED },
                '& .MuiPickersArrowSwitcher-button': {
                    color: '#fff',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    borderRadius: 1,
                },
                '& .MuiDayCalendar-weekDayLabel': { color: `${HELPER_MUTED} !important` },
                '& .MuiPickersDay-root': { color: '#fff' },
                '& .MuiPickersDay-root.Mui-disabled': { color: 'rgba(255,255,255,0.22)' },
                '& .MuiPickersDay-root.MuiPickersDay-dayOutsideMonth': {
                    color: 'rgba(255,255,255,0.28)',
                },
                '& .MuiPickersDay-root.Mui-selected': {
                    bgcolor: `${theme[themeStyle].highlight} !important`,
                    color: '#fff !important',
                    borderRadius: '999px',
                    '&:hover': { bgcolor: `${theme[themeStyle].highlight} !important` },
                },
                '& .MuiPickersDay-root.MuiPickersDay-today:not(.Mui-selected)': {
                    border: `1px solid ${theme[themeStyle].highlight}`,
                },
            },
        },
        dialog: {
            sx: {
                '& .MuiDialog-paper': {
                    bgcolor: PICKER_POPOVER_BG,
                    backgroundImage: 'none',
                    color: '#fff',
                },
            },
        },
    } as const;

    const toDayjs = (d: Date): Dayjs => (dayjs(d).isValid() ? dayjs(d) : dayjs());

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    position: 'absolute',
                    top: isMobile ? 0 : '50%',
                    left: isMobile ? 0 : '50%',
                    transform: isMobile ? 'none' : 'translate(-50%, -50%)',
                    width: isMobile ? '100%' : 'min(700px, 94vw)',
                    height: isMobile ? '100vh' : 'auto',
                    maxHeight: isMobile ? '100vh' : '90vh',
                    bgcolor: PAGE_BG,
                    boxShadow: 24,
                    borderRadius: isMobile ? 0 : '12px',
                    overflow: 'auto',
                    border: isMobile ? 'none' : '1px solid rgba(255,255,255,0.08)',
                }}>
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 3,
                        borderBottom: '1px solid rgba(255,255,255,0.08)',
                        position: 'sticky',
                        top: 0,
                        bgcolor: PAGE_BG,
                        zIndex: 2,
                    }}>
                    <Typography
                        component="div"
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: { xs: '1.25rem', sm: '1.5rem' },
                            fontWeight: 800,
                        }}>
                        {editingReview ? (
                            'Edit Review'
                        ) : (
                            <>
                                Review{' '}
                                <Box
                                    component="span"
                                    sx={{
                                        color: theme[themeStyle].highlight,
                                        fontStyle: 'italic',
                                    }}>
                                    {companyName}
                                </Box>
                            </>
                        )}
                    </Typography>
                    <IconButton onClick={onClose} sx={{ color: theme[themeStyle].tag_white }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Form Content */}
                <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
                    {!editingReview && (
                        <Typography
                            sx={{
                                color: HELPER_MUTED,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: '0.95rem',
                                mb: 3,
                                lineHeight: 1.5,
                            }}>
                            Share your professional experience to help the community.
                        </Typography>
                    )}

                    <Stack spacing={3}>
                        <Box>
                            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2 }}>
                                <EditNoteOutlined
                                    sx={{ color: theme[themeStyle].highlight, fontSize: 22 }}
                                    aria-hidden
                                />
                                <Typography component="h2" sx={sectionTitleRowSx(themeStyle)}>
                                    Review Details
                                </Typography>
                            </Stack>
                            <Typography component="label" sx={fieldLabelSx()}>
                                Review Subject
                            </Typography>
                    <TextField
                        fullWidth
                                hiddenLabel
                                variant="outlined"
                                placeholder="Summary of your experience..."
                        value={formData.review_subject}
                        onChange={e => handleInputChange('review_subject', e.target.value)}
                                inputProps={{ maxLength: 50, 'aria-label': 'Review subject' }}
                        helperText={`${formData.review_subject.length}/50 characters`}
                                sx={reviewTextFieldSx}
                            />
                        </Box>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Typography component="label" sx={fieldLabelSx()}>
                                    Pros *
                                </Typography>
                    <TextField
                        fullWidth
                                    hiddenLabel
                                    variant="outlined"
                        multiline
                        rows={4}
                                    placeholder="What went well?"
                        value={formData.pros}
                        onChange={e => handleInputChange('pros', e.target.value)}
                                    inputProps={{ maxLength: 1000, 'aria-label': 'Pros' }}
                        helperText={`${formData.pros.length}/1000 characters`}
                        required
                                    sx={reviewTextFieldSx}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography component="label" sx={fieldLabelSx()}>
                                    Cons
                                </Typography>
                    <TextField
                        fullWidth
                                    hiddenLabel
                                    variant="outlined"
                        multiline
                        rows={4}
                                    placeholder="What could be improved?"
                        value={formData.cons}
                        onChange={e => handleInputChange('cons', e.target.value)}
                                    inputProps={{ maxLength: 1000, 'aria-label': 'Cons' }}
                        helperText={`${formData.cons.length}/1000 characters`}
                                    sx={reviewTextFieldSx}
                                />
                            </Grid>
                        </Grid>

                        <Box>
                            <Typography component="label" sx={fieldLabelSx()}>
                                Role
                            </Typography>
                    <TextField
                        fullWidth
                                hiddenLabel
                                variant="outlined"
                                placeholder="e.g. Lead Actor, Stage Manager"
                        value={formData.role}
                        onChange={e => handleInputChange('role', e.target.value)}
                                inputProps={{ maxLength: 30, 'aria-label': 'Role' }}
                        helperText={`${formData.role.length}/30 characters`}
                                sx={reviewTextFieldSx}
                            />
                        </Box>

                        <Box
                            sx={{
                                bgcolor: PAGE_BG,
                                borderRadius: `${FIELD_RADIUS_PX}px`,
                                p: { xs: 2, sm: 2.5 },
                                border: '1px solid rgba(255,255,255,0.06)',
                            }}>
                            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2.5 }}>
                                <CalendarTodayOutlined
                                    sx={{ color: theme[themeStyle].highlight, fontSize: 22 }}
                                    aria-hidden
                                />
                                <Typography component="h2" sx={sectionTitleRowSx(themeStyle)}>
                            Employment Period
                        </Typography>
                            </Stack>

                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={formData.isCurrent ? 12 : 6}>
                                    <Typography component="label" htmlFor="review-start-date" sx={employmentDateLabelSx()}>
                                Start Date
                            </Typography>
                                    <DatePicker
                                        value={toDayjs(formData.startDate)}
                                        onChange={d => handleStartDateChange(d?.toDate() ?? new Date())}
                                        format="MM/DD/YYYY"
                                        closeOnSelect
                                        slotProps={{
                                            ...reviewDatePickerSlotProps,
                                            textField: {
                                                ...reviewDatePickerSlotProps.textField,
                                                id: 'review-start-date',
                                                inputProps: { 'aria-label': 'Start date' },
                                            },
                                        }}
                                    />
                                </Grid>
                                {!formData.isCurrent && (
                                    <Grid item xs={12} sm={6}>
                                        <Typography component="label" htmlFor="review-end-date" sx={employmentDateLabelSx()}>
                                            End Date
                                        </Typography>
                                        <DatePicker
                                            value={toDayjs(formData.endDate)}
                                            onChange={d => handleEndDateChange(d?.toDate() ?? new Date())}
                                            format="MM/DD/YYYY"
                                            minDate={toDayjs(formData.startDate)}
                                            closeOnSelect
                                            slotProps={{
                                                ...reviewDatePickerSlotProps,
                                                textField: {
                                                    ...reviewDatePickerSlotProps.textField,
                                                    id: 'review-end-date',
                                                    inputProps: { 'aria-label': 'End date' },
                                                },
                                            }}
                                        />
                                    </Grid>
                                )}
                            </Grid>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.isCurrent}
                                    onChange={e => handleCurrentEmployeeChange(e.target.checked)}
                                    sx={{
                                            p: 0.5,
                                            color: 'rgba(255,255,255,0.35)',
                                            '& .MuiSvgIcon-root': {
                                                fontSize: 22,
                                                borderRadius: '4px',
                                                border: '1px solid rgba(255,255,255,0.28)',
                                            },
                                        '&.Mui-checked': {
                                            color: theme[themeStyle].highlight,
                                        },
                                            '&.Mui-checked .MuiSvgIcon-root': {
                                                border: 'none',
                                        },
                                    }}
                                />
                            }
                            label={
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontWeight: 600,
                                    }}>
                                        I currently work here
                                </Typography>
                                }
                                sx={{ mt: 2, ml: 0, alignItems: 'center', gap: 1 }}
                            />
                    </Box>

                        <Box>
                            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2 }}>
                                <StarOutlineOutlined
                                    sx={{ color: theme[themeStyle].highlight, fontSize: 22 }}
                                    aria-hidden
                                />
                                <Typography component="h2" sx={sectionTitleRowSx(themeStyle)}>
                        Ratings
                    </Typography>
                            </Stack>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            {renderStarRating('Overall Rating', formData.rating, 'rating', value =>
                                handleInputChange('rating', value),
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                                    {renderStarRating(
                                        'Professionalism',
                                        formData.professionalism,
                                        'professionalism',
                                        value => handleInputChange('professionalism', value),
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            {renderStarRating('Satisfaction', formData.satisfaction, 'satisfaction', value =>
                                handleInputChange('satisfaction', value),
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            {renderStarRating(
                                'Creative Quality',
                                formData.creative_quality,
                                'creative_quality',
                                value => handleInputChange('creative_quality', value),
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            {renderStarRating('Culture', formData.culture, 'culture', value =>
                                handleInputChange('culture', value),
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            {renderStarRating(
                                'Payment Experience',
                                formData.payment_experience,
                                'payment_experience',
                                value => handleInputChange('payment_experience', value),
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            {renderStarRating('Reliability', formData.reliability, 'reliability', value =>
                                handleInputChange('reliability', value),
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            {renderStarRating('Would Recommend', formData.recommend, 'recommend', value =>
                                handleInputChange('recommend', value),
                            )}
                        </Grid>
                    </Grid>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1, pb: { xs: 6, sm: 1 } }}>
                            <Box sx={{ width: '100%', maxWidth: 440 }}>
                        <CustomButton
                            label={editingReview ? 'Update Review' : 'Submit Review'}
                            onClick={handleSubmit}
                            customStyle={{ width: '100%' }}
                            sx={{
                                '&:hover': {
                                            WebkitTextFillColor: theme[themeStyle].highlight,
                                },
                            }}
                            disabled={!formData.pros.trim()}
                        />
                    </Box>
                        </Box>
                    </Stack>
                </Box>
            </Box>
        </Modal>
    );
};

export { ReviewFormModal };
